import { parseSerieDescription } from './Schema';

// Which series carries the report; must stay in step with scripts/attach-reports.sh.

// Orthanc reserves < 1024 and DICOMweb uses 4301; mirrored in attach-reports.sh and build-data.py.
export const REPORT_ATTACHMENT_ID = 9999;

// Parses the AZmed series description of a candidate.
const describe = candidate =>
  parseSerieDescription(candidate.SeriesDescription || '');

// Orders candidates like the attach script's jq, so writer and reader pick the same series.
const bySeriesNumberThenUID = (a, b) => {
  const numberA = Number(a.SeriesNumber) || 0;
  const numberB = Number(b.SeriesNumber) || 0;

  if (numberA !== numberB) {
    return numberA - numberB;
  }

  // Code point order rather than localeCompare, to match jq.
  const uidA = String(a.SeriesInstanceUID);
  const uidB = String(b.SeriesInstanceUID);

  if (uidA === uidB) {
    return 0;
  }

  return uidA < uidB ? -1 : 1;
};

// Whether a candidate carries the AZmed schema; acquisition series do not.
const carriesSchema = candidate => {
  const { Langue, Certification } = describe(candidate);

  return Boolean(Langue && Certification);
};

// Study list card key; all five fields, as a reprocessing yields same-language series.
export const seriesGroupKey = ({
  Algorithm,
  Langue,
  Version,
  Certification,
  Option,
}) => `${Algorithm}|${Langue}|${Version}|${Certification}|${Option}`;

// Group key of a candidate, null when it carries no schema.
const groupKeyOf = candidate =>
  candidate && carriesSchema(candidate)
    ? seriesGroupKey(describe(candidate))
    : null;

// The SUMMARY of the ordered candidates, or failing that the first DUPLICATA.
const preferredOf = ordered =>
  ordered.find(c => describe(c).ImageType === 'SUMMARY') ||
  ordered.find(c => describe(c).ImageType === 'DUPLICATA');

/**
 * Report series of one group; never falls back to another group's document.
 * @param {Object[]} candidates Normalised series, from fromDisplaySets
 * @param {string|null} [groupKey] The group to resolve in, from groupKeyToResolveIn
 * @returns {Object|undefined}
 */
export const findReportSeriesOfGroup = (candidates, groupKey) => {
  if (!groupKey) {
    return undefined;
  }

  const group = (candidates || []).filter(
    candidate => groupKeyOf(candidate) === groupKey
  );

  // In-place sort is safe: filter returned a fresh array.
  return preferredOf(group.sort(bySeriesNumberThenUID));
};

// Distinct group keys among the candidates, in first-seen order.
const groupKeysOf = candidates =>
  new Set(candidates.map(groupKeyOf).filter(Boolean));

/**
 * Group to resolve the report in: the shown series' group, else the only group.
 * @param {Object[]} candidates Normalised series, from fromDisplaySets
 * @param {Object} [shown] The candidate on screen, from findShownCandidate
 * @returns {string|null}
 */
export const groupKeyToResolveIn = (candidates, shown) => {
  const shownKey = groupKeyOf(shown);

  if (shownKey) {
    return shownKey;
  }

  // Several groups is a refusal: picking could show a superseded version's report.
  const keys = groupKeysOf(candidates || []);

  return keys.size === 1 ? [...keys][0] : null;
};

/**
 * Candidate shown in the active viewport; undefined is not "no report".
 * @param {Object[]} candidates Normalised series, from fromDisplaySets
 * @param {string} [displaySetInstanceUID] The active viewport's display set
 * @returns {Object|undefined}
 */
export const findShownCandidate = (candidates, displaySetInstanceUID) =>
  displaySetInstanceUID
    ? (candidates || []).find(
        candidate => candidate.displaySetInstanceUID === displaySetInstanceUID
      )
    : undefined;

// Adapts display sets to candidates; the display set id is how the viewport names a series.
export const fromDisplaySets = displaySets =>
  (displaySets || []).map(displaySet => ({
    SeriesDescription: displaySet.SeriesDescription,
    SeriesNumber: displaySet.SeriesNumber,
    SeriesInstanceUID: displaySet.SeriesInstanceUID,
    displaySetInstanceUID: displaySet.displaySetInstanceUID,
  }));
